"use client";

import { c as _c } from "react/compiler-runtime";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useRouter } from "@/hooks/use-router";
import { analytics } from "@/lib/analytics/client";
import { getAuthErrorMessage, isAccountLinkingError } from "@domainstack/auth/errors";

/**
 * Hook to handle auth callback error query parameters.
 *
 * Automatically:
 * - Shows toast notifications for errors with user-friendly messages
 * - Cleans up the error query param from the URL
 *
 * @example
 * // In login page (sign-in callbacks)
 * useAuthCallback();
 *
 * @example
 * // In settings (account linking callbacks)
 * useAuthCallback();
 */
export function useAuthCallback() {
  const $ = _c(4);
  const router = useRouter();
  const searchParams = useSearchParams();
  const processedRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== router || $[1] !== searchParams) {
    t0 = () => {
      const error = searchParams.get("error");
      if (!error || processedRef.current) {
        return;
      }
      processedRef.current = true;
      const errorMessage = getAuthErrorMessage(error);
      const isLinkError = isAccountLinkingError(error);
      analytics.trackException(new Error(error), {
        action: isLinkError ? "link_account" : "sign_in",
        errorCode: error
      });
      const title = isLinkError ? "Failed to link account" : "Sign in failed";
      toast.error(title, {
        description: errorMessage
      });
      const params = new URLSearchParams(searchParams.toString());
      params.delete("error");
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
      router.replace(newUrl, {
        scroll: false
      });
    };
    t1 = [router, searchParams];
    $[0] = router;
    $[1] = searchParams;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}